/*************************************************
* MaxCap
 * @exports
 * index.tsx
 * Created by vinoth kumar on 08/05/2024
 * Copyright © 2024 MaxCap. All rights reserved.
 *************************************************/
// Import necessary modules
import { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import Loading from './../components/pages/common/Loading';
import store from "./../redux/SessionStorage";
import { getUserTypeFromSecureStorage } from "./../redux/services/secureStorage";

// Define a reusable Suspense wrapper component
const Loadable = (Component: any) => (props: any) => (
	<Suspense fallback={<Loading />}>
		<Component {...props} />
	</Suspense>
);

// Define your route components lazily
const Login = Loadable(lazy(() => import("../components/pages/Login")));
const ForgotPassword = Loadable(lazy(() => import("../components/pages/ForgotPassword")));
const ResetPassword = Loadable(lazy(() => import("../components/pages/ResetPassword")));
const Signup = Loadable(lazy(() => import("../components/pages/Signup")));
const SetPassword = Loadable(lazy(() => import("../components/pages/SetPassword")));
const OtpVerification = Loadable(lazy(() => import("../components/pages/OtpVerification")));
const Dashboard = Loadable(lazy(() => import("../components/pages/dashboard")));
const AppForm = Loadable(lazy(() => import("../components/pages/AppForm")));
const ErrorPage = Loadable(lazy(() => import("../components/pages/ErrorPage")));
const BankStatements = Loadable(lazy(() => import("../components/pages/BankStatements")));
const Users = Loadable(lazy(() => import("../components/pages/Users")));
const Lookup = Loadable(lazy(() => import("../components/pages/Lookup")));
const EditUser = Loadable(lazy(() => import("../components/pages/EditUser")));
const OpenBanking = Loadable(lazy(() => import("../components/pages/OpenBanking")));

const PrivateRoute = ({ children }: any) => {
	const user = store.get("user")
		? JSON.parse(store.get("user") || "")
		: null;
	if (!user) {
		return <Navigate to="/login" />;
	}
	return children;
};

const AdminRoute = ({ children }: any) => {
	const user = store.get("user")
		? JSON.parse(store.get("user") || "")
		: null;
	const type = getUserTypeFromSecureStorage();
	if (type !== "admin" || !user) {
		return <Navigate to="/login" />;
	}
	return children;
};

// Define your route configuration within the context of Router
export default function AppRouter() {
	return (
		<Router  future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
			<Routes>
				<Route path="/login" element={<Login />} />
				<Route path="/forgot-password" element={<ForgotPassword />} />
				<Route path="/reset-password" element={<ResetPassword />} />
				<Route path="/signup" element={<Signup />} />
				<Route path="/set-password" element={<SetPassword />} />
				<Route path="/otp-verify" element={<OtpVerification />} />
				<Route path="/error" element={<ErrorPage />} />
				<Route path="/dashboard"
					element={
						<PrivateRoute>
							<Dashboard />
						</PrivateRoute>
					} />
				<Route path="/apply-now"
					element={
						<PrivateRoute>
							<AppForm />
						</PrivateRoute>
					} />
				<Route path="/bank-statements"
					element={
						<PrivateRoute>
							<BankStatements />
						</PrivateRoute>
					} />
				<Route path="/users"
					element={
						<AdminRoute>
							<Users />
						</AdminRoute>
					} />
				<Route path="/lookup"
					element={
						<AdminRoute>
							<Lookup />
						</AdminRoute>
					} />
				<Route path="/edit-user"
					element={
						<AdminRoute>
							<EditUser />
						</AdminRoute>
					} />
				<Route path="/openbanking"
					element={
						<OpenBanking />
					} />
				<Route path="/" element={<Navigate to="/login" />} />
				<Route path="*" element={<Navigate to="/login" />} />

			</Routes>
		</Router>
	);
}
